import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUploadAlt,
  faFile,
  faFileInvoice,
  faReceipt,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import UploadedDocumentItem from '../DocumentItem/UploadedDocumentItem';
import api from '../../services/api';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const getAttachmentIcon = (alias) => {
  switch (alias) {
    case 'invoice':
      return faFileInvoice;
    case 'deliveryNote':
      return faTruck;
    case 'receipt':
    case 'paymentProof':
      return faReceipt;
    default:
      return faFile;
  }
};

/**
 * Anexos adicionales de venta (Factura, Remito, etc.) — backend: sale_files.
 */
const SaleAttachmentsSection = ({ customerId, saleId, onViewDocument }) => {
  const [categories, setCategories] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadData = useCallback(async () => {
    if (!customerId || !saleId) return;
    setLoading(true);
    try {
      const [catsRes, attRes] = await Promise.all([
        api.get('/sales/attachment-file-categories'),
        api.get(`/customers/${customerId}/sales/${saleId}/attachments`),
      ]);
      const cats = catsRes.data.categories || [];
      setCategories(cats);
      setAttachments(attRes.data.attachments || []);
      setSelectedCategory((prev) => {
        if (prev && cats.some((c) => c.alias === prev)) return prev;
        return cats[0]?.alias || '';
      });
    } catch (error) {
      console.error('Error loading sale attachments:', error);
      MySwal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'No se pudieron cargar los adjuntos',
        icon: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, saleId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpload = useCallback(
    async (file) => {
      if (!file || !selectedCategory) return;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', selectedCategory);
        await api.post(`/customers/${customerId}/sales/${saleId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        MySwal.fire({
          title: 'Archivo anexado',
          text: 'El documento se guardó correctamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
        await loadData();
      } catch (error) {
        console.error('Error uploading sale attachment:', error);
        MySwal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo subir el archivo',
          icon: 'error',
        });
      } finally {
        setUploading(false);
      }
    },
    [selectedCategory, customerId, saleId, loadData]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file && !uploading) {
        handleUpload(file);
      }
    },
    [uploading, handleUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: uploading || !selectedCategory,
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
  });

  const openViewer = (att) => {
    onViewDocument({
      title: att.name,
      file: { name: att.name, url: att.url },
    });
  };

  return (
    <div className="mt-4 pt-3 border-top">
      <h6 className="mb-3">Adjuntos adicionales</h6>
      <p className="text-muted small mb-3">
        Factura, remito, recibo u otros. Arrastrá el archivo o hacé clic para elegirlo.
      </p>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <Form.Label className="small text-muted">Categoría</Form.Label>
          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={loading || categories.length === 0}
            className="form-select"
          >
            {categories.length === 0 && !loading ? (
              <option value="">Sin categorías disponibles</option>
            ) : (
              categories.map((c) => (
                <option key={c.id} value={c.alias}>
                  {c.name}
                </option>
              ))
            )}
          </Form.Select>
        </div>
      </div>

      <div
        {...getRootProps()}
        className={`border rounded-3 p-4 text-center mb-3 ${
          isDragActive ? 'border-success bg-light' : 'border-secondary'
        } ${uploading || !selectedCategory ? 'opacity-50' : 'cursor-pointer'}`}
        style={{ cursor: uploading || !selectedCategory ? 'not-allowed' : 'pointer' }}
      >
        <input {...getInputProps()} />
        <FontAwesomeIcon icon={faCloudUploadAlt} size="2x" className="text-secondary mb-2" />
        <p className="mb-0 small text-muted">
          {uploading
            ? 'Subiendo…'
            : !selectedCategory
              ? 'Seleccioná una categoría primero'
              : isDragActive
                ? 'Soltá el archivo aquí'
                : 'Arrastrá un archivo o hacé clic para seleccionar (PDF, JPG, PNG)'}
        </p>
      </div>

      {loading ? (
        <div className="text-muted small">Cargando adjuntos…</div>
      ) : (
        <ul className="document-list">
          {attachments.map((att) => {
            const alias = att.categories?.[0]?.alias;
            const label = att.categories?.[0]?.name;
            const displayName = label ? `${label}` : att.name;
            return (
              <UploadedDocumentItem
                key={att.id}
                icon={getAttachmentIcon(alias)}
                name={displayName}
                uploadDate={att.created_at}
                onView={() => openViewer(att)}
                onDownload={() => window.open(att.url, '_blank')}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SaleAttachmentsSection;
